import React, { useState, useEffect, useMemo } from 'react'
import { useCoachChat } from './CoachChatContext'
import AudioParticipantCard from '../shared/AudioParticipantCard'

/**
 * CoachAudioConversation — coach audio chat layout (CTS-9778 card redesign).
 */
const CoachAudioConversation = ({ candidate }) => {
  const {
    isDarkMode,
    connectionState,
    chatManager,
    activeChats,
    userDisplayName,
    userFirstName,
    username,
    avatar,
    bigAvatar,
  } = useCoachChat()

  const [audioBlocked, setAudioBlocked] = useState(false)
  const [remoteAudioMutedFromEvent, setRemoteAudioMutedFromEvent] =
    useState(null)

  const localStream = chatManager?.connectionState?.localStream || null
  const remoteStream = chatManager?.connectionState?.remoteStream || null

  const hasLocalAudio =
    connectionState?.hasLocalAudio ||
    (localStream &&
      localStream.getAudioTracks &&
      localStream.getAudioTracks().length > 0) ||
    false
  const localAudioMuted = connectionState?.audioMuted || false

  const activeChat = useMemo(
    () =>
      (activeChats || connectionState?.activeChats || []).find(
        (chat) =>
          chat.candidateEasyrtcid === candidate?.easyrtcid ||
          chat.candidateId === candidate?.easyrtcid,
      ),
    [activeChats, candidate?.easyrtcid, connectionState?.activeChats],
  )

  const remoteAudioMuted =
    remoteAudioMutedFromEvent ?? Boolean(activeChat?.candidateAudioMuted)

  const localPerson = useMemo(
    () => ({
      displayName: userDisplayName || username,
      firstName: userFirstName,
      avatar,
      bigAvatar,
    }),
    [avatar, bigAvatar, userDisplayName, userFirstName, username],
  )

  useEffect(() => {
    const handleCandidateMediaStateChange = (data) => {
      if (
        data.candidateEasyrtcid === candidate?.easyrtcid &&
        (data.messageType === 'audio_muted' ||
          data.messageType === 'audio_unmuted')
      ) {
        setRemoteAudioMutedFromEvent(data.messageType === 'audio_muted')
      }
    }

    if (chatManager) {
      chatManager.addEventListener(
        'candidateMediaStateChanged',
        handleCandidateMediaStateChange,
      )
    }

    return () => {
      if (chatManager) {
        chatManager.removeEventListener(
          'candidateMediaStateChanged',
          handleCandidateMediaStateChange,
        )
      }
    }
  }, [candidate?.easyrtcid, chatManager])

  useEffect(() => {
    const handleAutoplayBlocked = () => setAudioBlocked(true)

    if (chatManager?.rtcService) {
      chatManager.rtcService.addEventListener(
        'autoplayBlocked',
        handleAutoplayBlocked,
      )
    }

    return () => {
      if (chatManager?.rtcService) {
        chatManager.rtcService.removeEventListener(
          'autoplayBlocked',
          handleAutoplayBlocked,
        )
      }
    }
  }, [chatManager])

  useEffect(() => {
    if (remoteStream) {
      let videoElement = document.getElementById('callerVideo')
      if (!videoElement) {
        videoElement = document.createElement('video')
        videoElement.id = 'callerVideo'
        videoElement.style.display = 'none'
        videoElement.muted = false
        videoElement.volume = 1.0
        videoElement.autoplay = true
        videoElement.playsInline = true
        document.body.appendChild(videoElement)
      } else {
        videoElement.muted = false
        videoElement.volume = 1.0
      }

      if (videoElement.srcObject !== remoteStream) {
        videoElement.srcObject = remoteStream
      }

      videoElement.play().catch(() => setAudioBlocked(true))
    }
  }, [remoteStream])

  const handleEnableAudio = () => {
    const videoElement = document.getElementById('callerVideo')
    if (videoElement) {
      return videoElement
        .play()
        .then(() => {
          setAudioBlocked(false)
          return true
        })
        .catch(() => false)
    }
    return Promise.resolve(false)
  }

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {/* Local coach */}
      <AudioParticipantCard
        role="coach"
        person={localPerson}
        audioStream={hasLocalAudio ? localStream : null}
        isMuted={localAudioMuted}
        isDarkMode={isDarkMode}
        showVisualizer={hasLocalAudio}
      />

      {/* Remote candidate */}
      <AudioParticipantCard
        role="candidate"
        person={candidate}
        audioStream={remoteStream}
        isMuted={remoteAudioMuted}
        isDarkMode={isDarkMode}
        footer={
          audioBlocked ? (
            <button
              type="button"
              onClick={handleEnableAudio}
              className="rounded-md bg-[#08467c] px-4 py-2 font-roboto text-sm font-medium text-white hover:bg-[#06355f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#08467c]"
            >
              Click to Enable Audio
            </button>
          ) : null
        }
      />
    </div>
  )
}

export default CoachAudioConversation
